"use client";

import React from "react";
import { Phone, Mail, MapPin, Clock, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const contactDetails = [{
  icon: Phone,
  title: "Phone",
  content: "+86 13927478668",
  link: "tel:+8613927478668"
}, {
  icon: Mail,
  title: "Email",
  content: "ivy@hysorter.com",
  link: "mailto:ivy@hysorter.com"
}, {
  icon: MapPin,
  title: "Office",
  content: "No. 7, Dongmao Road, Guangming District, Shenzhen",
  link: null
}, {
  icon: Clock,
  title: "Business Hours",
  content: "Mon-Sat: 9:00 AM - 6:00 PM",
  link: null
}];
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center bg-accent/50 rounded-xl border border-border">
        <div className="size-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
          <Mail className="size-6 text-primary" />
        </div>
        <h3 className="text-xl font-default font-semibold text-foreground mb-2">Message Sent</h3>
        <p className="text-muted-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel>Phone (Optional)</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel>Company (Optional)</FormLabel>
              <FormControl>
                <Input placeholder="Acme Inc." {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="subject" render={({
      field
    }) => <FormItem>
            <FormLabel>Subject (Optional)</FormLabel>
            <FormControl>
              <Input placeholder="How can we help you?" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Message</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us more about your inquiry..." className="min-h-[150px] resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-sm text-destructive font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[160px]">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Sending...
          </> : "Send Message"}
      </Button>
    </Form>;
}
export default function ContactForm() {
  return <section id="contactform" className="py-16 md:py-24 bg-background text-foreground light">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="mb-12 text-center md:text-left">
          <h2 className="text-3xl md:text-4xl font-default font-bold tracking-tight mb-4">
            Get in Touch
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl">
            Have a question or want to work together? Fill out the form below and our team will get back to you as soon as possible.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-8">
          <div className="lg:col-span-2 order-2 lg:order-1">
            <div className="bg-card text-card-foreground rounded-xl border border-border shadow-sm p-6 md:p-8">
              <FormProvider formId="main-contact-form" sectionName="ContactForm" defaultValues={{
              name: "",
              email: "",
              phone: "",
              company: "",
              subject: "",
              message: ""
            }} submitText="Send Message" successMessage="Thank you for reaching out! We have received your message and will contact you shortly.">
                <ContactFormInner />
              </FormProvider>
            </div>
          </div>

          <div className="lg:col-span-1 order-1 lg:order-2 space-y-6">
            {contactDetails.map((detail, i) => {
            const Icon = detail.icon;
            const content = <CardContent className="p-6 flex items-start gap-4">
                  <div className="bg-primary/10 p-3 rounded-lg shrink-0">
                    <Icon className="size-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-default font-semibold text-base mb-1">{detail.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {detail.content}
                    </p>
                  </div>
                </CardContent>;
            return <Card key={i} data-index={i} className="overflow-hidden border-border shadow-sm hover:shadow-md transition-shadow">
                  {detail.link ? <Link to={detail.link} className="block h-full hover:bg-accent/50 transition-colors">
                      {content}
                    </Link> : content}
                </Card>;
          })}
          </div>
        </div>
      </div>
    </section>;
}